import React, { useMemo } from 'react';
import { Modal, Pressable, Text, View, useWindowDimensions } from 'react-native';
import { BlurView } from 'expo-blur';
import { useColorScheme } from 'nativewind';
import RenderHtml from 'react-native-render-html';
import { Icon } from '../index';
import svgIcons from '../../assets';
import { isMobile, isNative } from '../../utils/responsive';
import { handleLinkPress } from '../../utils';
import { highlightHtml } from '../../utils/highlightHtml';

const MemoizedRenderHtml = React.memo(RenderHtml);

interface Props {
  showModal: boolean;
  closeModal: () => void;
  title: string;
  description: string;
  search?: string;
}

const HelpdeskIssueDetailModal: React.FC<Props> = ({
  showModal,
  closeModal,
  title,
  description,
  search,
}) => {
  const { colorScheme } = useColorScheme();
  const isDark = colorScheme === 'dark';
  const textColor = isDark ? '#FAFAFA' : '#101010';
  const { width } = useWindowDimensions();
  const baseStyle = useMemo(
    () => ({
      color: textColor,
      fontSize: 16,
      fontFamily: 'sans-400',
      lineHeight: 24,
    }),
    [textColor],
  );
  const renderersProps = useMemo(
    () => ({
      a: {
        onPress: (_event, href) => handleLinkPress(href),
      },
    }),
    [],
  );
  const tagsStyles = useMemo(
    () => ({
      a: {
        color: isDark ? '#EDF115' : '#623BFF',
        textDecorationLine: 'none' as const,
      },
      mark: {
        backgroundColor: isDark ? '#EDF115' : '#8509ff',
        color: isDark ? '#101010' : '#FAFAFA',
      },
    }),
    [isDark],
  );

  return (
    <Modal
      animationType="fade"
      presentationStyle="overFullScreen"
      visible={showModal}
      transparent={true}
    >
      <BlurView
        style={{
          height: '100%',
          width: '100%',
          backgroundColor:
            colorScheme === 'dark' ? 'rgba(16,16,16,0.35)' : 'rgba(246,246,246,0.35)',
        }}
        intensity={isNative ? 30 : 40}
        tint={colorScheme}
        experimentalBlurMethod="dimezisBlurView"
      >
        <View style={{ flex: 1 }}>
          <Pressable
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            onPress={closeModal}
          />
          <View
            pointerEvents="box-none"
            style={{
              paddingTop: 80,
              alignItems: 'center',
              paddingHorizontal: isMobile ? 32 : 0,
            }}
          >
            <View style={isMobile ? { width: '100%' } : { width: 584 }}>
              <View
                testID="helpdesk-issue-detail-modal"
                className="bg-neutral-50 dark:bg-neutral-950 flex-row"
                style={{
                  gap: 16,
                  paddingHorizontal: 24,
                  paddingTop: isMobile ? 24 : 40,
                  paddingBottom: isMobile ? 24 : 84,
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text className="font-[sans-700] text-lead mb-2" style={{ color: textColor }}>
                    {title}
                  </Text>
                  <MemoizedRenderHtml
                    contentWidth={width}
                    source={{ html: highlightHtml(description, search) }}
                    baseStyle={baseStyle}
                    renderersProps={renderersProps}
                    tagsStyles={tagsStyles}
                  />
                </View>
                <Pressable onPress={closeModal} hitSlop={8}>
                  <Icon
                    icon={svgIcons.closeIcon}
                    mobileSize={{ width: 24, height: 24 }}
                    webSize={{ width: 24, height: 24 }}
                  />
                </Pressable>
              </View>
            </View>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
};

export default HelpdeskIssueDetailModal;
